import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useOne } from "@refinedev/core";
import Button from "components/button"
import ColorPicker from "components/colorPicker";
import DateTimePicker from "components/datetimePicker";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps } from "components/modal"
import RadioButton from "components/radioButton";
import { RightSideModal } from "components/rightSideModal"
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import { TextEditor } from "components/textEditor";
import { IMultilingualContent, ISeatLayoutDropdownItem, ISeatmapNoParentTicketForm, ISimpleEventDetail, ISubTicketItem, PriceType, ProductType, SeatLayoutType, SupportedLanguage } from "interfaces"
import { get, isEmpty } from "lodash";
import moment from "moment";
import { useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { AUTO_SEATING_CHARACTER_POSITIONS, LANGUAGES, PRICE_TYPES, SEAT_LAYOUT_TYPES } from "utills/constants";
import { NoParentTicketSchema } from "../schema";

interface NoParentTicketFormProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  onSubmit: (data: ISeatmapNoParentTicketForm) => void;
  productType: ProductType;
  defaultValues: ISubTicketItem | null;
}

interface IAttendantTemplateField {
  id: string;
  template_id: string;
  name: IMultilingualContent;
}

const DEFAULLT_TEMPLATE = [{
  template_id: "",
  name: {
    en: "",
    vi: ""
  }
}]

const getDefaultTicketValue = (defaultValues: ISubTicketItem | null) => {
  return {
    category: defaultValues?.category ?? {
      en: "",
      vi: ""
    },
    selling_min_qty: defaultValues?.selling_min_qty ?? 1,
    selling_max_qty: defaultValues?.selling_max_qty ?? 10,
    is_package: defaultValues?.is_package ?? false,
    attendant_template: defaultValues?.attendant_template ?? DEFAULLT_TEMPLATE,
    type_name: defaultValues?.type_name ?? {
      en: "",
      vi: ""
    },
    short_description: defaultValues?.short_description ?? {
      en: "",
      vi: ""
    },
    price_type: defaultValues?.price_type ?? PriceType.PAID,
    original_price: defaultValues?.original_price ?? 0,
    price: defaultValues?.price ?? 0,
    sell_from: defaultValues
      ? defaultValues?.sell_from
        ? moment(defaultValues.sell_from).format("YYYY-MM-DD HH:mm:ss")
        : ""
      : moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
    sell_to: defaultValues
      ? defaultValues?.sell_to
        ? moment(defaultValues.sell_to).format("YYYY-MM-DD HH:mm:ss")
        : ""
      : moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
    is_soldout: defaultValues?.is_soldout ?? false,
    is_active: defaultValues?.is_active ?? true,
    ticket_phase: defaultValues?.ticket_phase ?? "",
    hide_in_ui: defaultValues?.hide_in_ui ?? false,
    total_for_sell: defaultValues?.total_for_sell ?? null,
    is_auto_seating: defaultValues?.is_auto_seating ?? false,
    start_seat_number: defaultValues?.start_seat_number ?? null,
    auto_seating_leading_zero_spaces: defaultValues?.auto_seating_leading_zero_spaces ?? null,
    auto_seating_add_characters: defaultValues?.auto_seating_add_characters ?? false,
    auto_seating_characters: defaultValues?.auto_seating_characters ?? "",
    auto_seating_character_position: defaultValues?.auto_seating_character_position ?? "before",
    layout_type: defaultValues?.layout_type ?? SeatLayoutType.ZONE,
    seat_layout_id: defaultValues?.seat_layout_id ?? null,
    category_color: defaultValues?.category_color ?? null,

  }
}
export const NoParentTicketForm = ({open, onClose, eventDetailData, onSubmit, productType, defaultValues, title}: NoParentTicketFormProps) => {
  const { data } = useOne({
    resource: `content/attendant-templates/events`,
    id: eventDetailData.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(eventDetailData.id && open),
    } as any,
  });

  const { data: seatLayoutData, isFetching: isFetchingLayout } = useOne<ISeatLayoutDropdownItem[], HttpError>({
    resource: "content/dropdown",
    id: "layout-seat",
    meta: {
      resource: "data",
      filters: [
        { field: "event_id", operator: "eq", value: eventDetailData.id },
        { field: "available", operator: "eq", value: "true" },
        { field: "layout_id", operator: "eq", value: defaultValues?.seat_layout_id ?? "" },
      ]
    },
    queryOptions: {
      enabled: !!(eventDetailData.id && open),
    } as any,
  });
  
  const templatesDataArr = (data?.data ?? []).map((template: any) => ({
    label: get(template, "attendant_template_name", ""),
    value: get(template, "id", "")
  }))

  const seatLayoutArr = (seatLayoutData?.data ?? []).map((layout: any) => ({
    label: get(layout, "layout_name", ""),
    value: get(layout, "id", "")
  }))

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ISeatmapNoParentTicketForm, HttpError, ISeatmapNoParentTicketForm>({
    defaultValues: getDefaultTicketValue(defaultValues),
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        NoParentTicketSchema(eventDetailData.languages)
      );
      return createResolver(values, context, options);
    },
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultTicketValue(defaultValues));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])


  const { fields, append, remove } = useFieldArray({
    control,
    name: "attendant_template"
  });

  const _submit = (data: ISeatmapNoParentTicketForm) => {
    onSubmit({
      ...data,
      product_type: productType
    });
  }

  const _resetAutoSeatingFields = () => {
    setValue("start_seat_number", null);
    setValue("auto_seating_leading_zero_spaces", null);
    setValue("auto_seating_add_characters", false);
    setValue("auto_seating_character_position", "before");
    setValue("auto_seating_characters", "");
  }
  
  const isOpenRegistration = get(eventDetailData, "open_registration_date", "") 
    ? moment().utc().isAfter(moment(eventDetailData.open_registration_date).utc())
    : false;
  
  return (
    <RightSideModal title={title ?? "Add Ticket"} open={open} onClose={onClose} footer={(
      <div className="w-full flex flex-row gap-x-2 items-center">
        <Button 
          className="w-full bg-white border-none text-brand-500 ring-1 ring-inset ring-brand-500 hover:bg-gray-50 active:bg-gray-100" 
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button className="w-full uppercase" onClick={handleSubmit(_submit)}>Save</Button>
      </div>
    )}>
      <div className="p-4 sm:px-6">
        <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4">
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">Ticket Info</p>
            {defaultValues?.id ? <p className="text-sm mt-1">Product ID: {defaultValues?.id}</p> : <></>}
          </div>
          <div className="col-span-full">
            <Controller
              control={control}
              name="layout_type"
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <div className="flex flex-row flex-wrap gap-x-3">
                    {SEAT_LAYOUT_TYPES.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        disabled={isOpenRegistration && (defaultValues ? true : false)}
                        className={`rounded-md ${type.value === value ? "bg-[#A7B4FE] hover:bg-indigo-300 focus-visible:outline-indigo-300" : "bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50"} px-6 py-3 text-sm font-semibold text-brand-500 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                        onClick={() => {
                          onChange(type.value);
                          if (type.value === SeatLayoutType.SEAT) {
                            setValue("total_for_sell", null);
                            setValue("is_auto_seating", false);
                            _resetAutoSeatingFields();
                          }
                          if (type.value === SeatLayoutType.ZONE) {
                            setValue("seat_layout_id", null);
                          }
                        }}
                      >
                        {type.label}
                      </button>
                    
                    ))}
                  </div>
                );
              }}
            />
            {get(
              errors,
              `layout_type`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `layout_type.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>
          <div className="col-span-full">
            <Label
              required
              id={`category_${eventDetailData.languages[0] ?? "en"}`}
              name="Category"
            />
            <div className="flex flex-col gap-y-4">
              {eventDetailData.languages.map((lang) => (
                <div key={lang}>
                  <div className="relative" key={lang}>
                    <Input
                      id={`category_${lang}`}
                      type="text"
                      className="pr-11"
                      placeholder={`Cateogry (${lang})`}
                      {...register(`category.${lang}`)}
                      error={get(
                        errors,
                        `category.${lang}.message`,
                        ""
                      )}
                    />
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                  {get(
                    errors,
                    `category.${lang}`,
                    ""
                  ) ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(
                          errors,
                          `category.${lang}.message`,
                          ""
                        )}
                      </span>
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-full">
            <Label
              required
              id={`type_name_${eventDetailData.languages[0] ?? "en"}`}
              name="Ticket name displayed"
            />
            <div className="flex flex-col gap-y-4">
              {eventDetailData.languages.map((lang) => (
                <div key={lang}>
                  <div className="relative" key={lang}>
                    <Input
                      id={`type_name_${lang}`}
                      type="text"
                      placeholder={`Ticket name displayed (${lang})`}
                      className="pr-11"
                      {...register(`type_name.${lang}`)}
                      error={get(
                        errors,
                        `type_name.${lang}.message`,
                        ""
                      )}
                    />
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                  {get(
                    errors,
                    `type_name.${lang}`,
                    ""
                  ) ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(
                          errors,
                          `type_name.${lang}.message`,
                          ""
                        )}
                      </span>
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          {watch("layout_type") === SeatLayoutType.SEAT ? (
            <div className="col-span-full">
              <Label
                id="seat_layout_id"
                name="Seat layout"
                required
              />
              <Controller
                control={control}
                name="seat_layout_id"
                render={({ field: { onChange, value = "", ref } }) => {
                  return (
                    <Select
                      key={`seat_layout_id__${watch("layout_type")}`}
                      inputRef={ref}
                      options={seatLayoutArr}
                      isLoading={isFetchingLayout}
                      id="seat_layout_id"
                      placeholder="Seat layout"
                      isDisabled={isOpenRegistration && (defaultValues ? true : false)}
                      defaultValue={
                        seatLayoutArr.find(
                          (selected: { label: string; value: string }) =>
                            selected.value === value
                        ) ?? ""
                      }
                      value={seatLayoutArr.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === value
                      )}
                      onChange={(selected: { label: string; value: string }) => {
                        onChange(selected?.value);
                      }}
                      error={get(errors, "seat_layout_id", "")}
                    />
                  );
                }}
              />
              {get(errors, "seat_layout_id", "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, "seat_layout_id.message" ,"")}
                  </span>
                </p>
              ) : null}
            </div>
          ) : <></>}
          <div className="col-span-full">
            <Input
              type="checkbox"
              label="Category Color"
              id="category_color"
              checked={!isEmpty(watch("category_color"))}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const isChecked = e.target.checked;
                if (isChecked) {
                  setValue("category_color", "#000000");
                } else {
                  setValue("category_color", null);
                }
              }}    
            />
            {!isEmpty(watch("category_color")) ? (
              <Controller
                control={control}
                name="category_color"
                render={({ field: { onChange, value = "" } }) => {
                  return (
                    <div className="relative mt-4">
                      <ColorPicker value={value} onChange={onChange} placeholder="Category Color" />
                    </div>
                  )
                }}
              />
            ) : <></>}
            
            {get(errors, "category_color", "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, "category_color.message" ,"")}
                </span>
              </p>
            ) : null}
          </div>
          <div className="col-span-full">
            <Label
              id={`short_description_${eventDetailData.languages[0] ?? "en"}`}
              name="Note below Ticket name"
            />
            <div className="flex flex-col gap-y-4">
              {eventDetailData.languages.map(
                (lang: SupportedLanguage) => (
                  <div key={lang}>
                    <div
                      className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                    >
                      <img
                        src={`/images/flags/circle-${lang}.png`}
                        alt="Language Icon"
                        className="w-4 shrink-0"
                      />
                      <p>
                        {
                          LANGUAGES.find(
                            (language) =>
                              language.value === lang
                          )?.label
                        }
                      </p>
                    </div>

                    <Controller
                      control={control}
                      name={`short_description.${lang}`}
                      render={({
                        field: { onChange, value },
                      }) => (
                        <TextEditor
                          key={`short_description.${lang}`}
                          value={value ?? ""}
                          onChange={onChange}
                        />
                      )}
                    />

                    {get(
                      errors,
                      `short_description.${lang}`,
                      ""
                    ) ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(
                            errors,
                            `short_description.${lang}.message`,
                            ""
                          )}
                        </span>
                      </p>
                    ) : null}
                  </div>
                )
              )}
            </div>
          </div>
          <div className="sm:col-span-3">
            <Label
              id="selling_min_qty"
              name="Select min quantity"
              required
            />
            <Input
              id="selling_min_qty"
              type="number"
              min={0}
              placeholder={`Select min quantity`}
              {...register(`selling_min_qty`, {
                valueAsNumber: true
              })}
              error={get(
                errors,
                `selling_min_qty.message`,
                ""
              )}
            />
            {get(
              errors,
              `selling_min_qty`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `selling_min_qty.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="selling_max_qty"
              name="Select max quantity"
              required
            />
            <Input
              id="selling_max_qty"
              type="number"
              min={0}
              placeholder={`Select max quantity`}
              {...register(`selling_max_qty`, {
                valueAsNumber: true
              })}
              error={get(
                errors,
                `selling_max_qty.message`,
                ""
              )}
            />
            {get(
              errors,
              `selling_max_qty`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `selling_max_qty.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>
          {watch("layout_type") === SeatLayoutType.ZONE ? (
            <div className="col-span-full">
              <Label
                id="total_for_sell"
                name="Total for sell"
                required={watch("is_auto_seating") ? true : false}
              />
              <Input
                id="total_for_sell"
                type="number"
                min={0}
                placeholder={`Total for sell`}
                {...register(`total_for_sell`, {
                  valueAsNumber: true
                })}
                error={get(
                  errors,
                  `total_for_sell.message`,
                  ""
                )}
              />
              {get(
                errors,
                `total_for_sell`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `total_for_sell.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>
          ) : <></>}
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">Price Info</p>
          </div>
          <div className="col-span-full">
            <Controller
              control={control}
              name="price_type"
              render={({ field: { onChange, value = "", ref } }) => {
                return (
                  <div className="flex flex-row flex-wrap gap-x-3">
                    {PRICE_TYPES.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        className={`rounded-md ${type.value === value ? "bg-[#A7B4FE] hover:bg-indigo-300 focus-visible:outline-indigo-300" : "bg-white ring-1 ring-inset ring-gray-300 hover:bg-gray-50"} px-6 py-3 text-sm font-semibold text-brand-500 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2`}
                        onClick={() => {
                          onChange(type.value)
                          if (type.value === PriceType.FREE) {
                            setValue("price", 0, {shouldValidate: true});
                            setValue("original_price", 0, {shouldValidate: true});
                          } 
                        }}
                      >
                        {type.label}
                      </button>
                    
                    ))}
                  </div>
                );
              }}
            />
            {get(
              errors,
              `price_type`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `price_type.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label
              id="original_price"
              name="Original price"
              required
            />
            <div className="flex">
              <Input
                id="original_price"
                type="number"
                decimal={2}
                className="rounded-r-none"
                placeholder="Original price"
                disabled={watch("price_type") === PriceType.FREE}
                error={get(errors, "original_price")}
                {...register("original_price", {
                  valueAsNumber: true
                })}  
              />
              <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                {eventDetailData.currency}
              </span>
            </div>
            {get(
              errors,
              `original_price`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `original_price.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="price"
              name="Sell price"
              required
            />
            <div className="flex">
              <Input
                id="price"
                type="number"
                decimal={2}
                className="rounded-r-none"
                placeholder="Sell price"
                disabled={watch("price_type") === PriceType.FREE}
                error={get(errors, "price")}
                {...register("price", {
                  valueAsNumber: true
                })}  
              />
              <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                {eventDetailData.currency}
              </span>
            </div>
            {get(
              errors,
              `price`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `price.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="sell_from"
              name="Start date"
              required
            />
            <Controller
              control={control}
              name="sell_from"
              render={({ field: { onChange, value = "" } }) => {
                return (
                  <DateTimePicker
                    id="sell_from"
                    placeholder="Start date"
                    error={get(
                      errors,
                      `sell_from.message`,
                      ""
                    )}
                    value={value}
                    onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                  />
                );
              }}
            />
            {get(
              errors,
              `sell_from`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `sell_from.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="sell_to"
              name="End date"
              required
            />
            <Controller
              control={control}
              name="sell_to"
              render={({ field: { onChange, value = "" } }) => {
                return (
                  <DateTimePicker
                    id="sell_to"
                    placeholder="End date"
                    error={get(
                      errors,
                      `sell_to.message`,
                      ""
                    )}
                    value={value}
                    onChange={(date: any) => date === null ? onChange("") : onChange(moment(date).format("YYYY-MM-DD HH:mm:ss"))}
                  />
                );
              }}
            />
            {get(
              errors,
              `sell_to`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `sell_to.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">Participant form Info</p>
          </div>
          <div className="col-span-full">
            {watch("is_package") ? (
              <button
                type="button"
                className="flex flex-row items-center w-fit text-sm bg-white text-brand-500 active:bg-gray-50"
                onClick={() => append(DEFAULLT_TEMPLATE)}
              >
                <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                <span className="shrink-0 text-sm">Add Participant forms</span>
              </button>
            ) : <></>}
            <div className="divide-y divide-gray-300">
              {fields.map((field: IAttendantTemplateField, index: number) => (
                <div key={field.id} className="py-4">
                  {(watch("is_package") && fields.length > 1) ? (
                    <div className="col-span-full flex justify-end">
                      <button onClick={() => remove(index)}>
                        <TrashIcon className="w-6 h-6 text-brand-500" />
                      </button>
                    </div>
                  ) : <></>}
                  <div>
                    <Label
                      id={`attendant_template.${index}.template_id`}
                      name="Select Participant Form"
                      required
                    />
                    <Controller
                      control={control}
                      name={`attendant_template.${index}.template_id`}
                      render={({ field: { onChange, value = "", ref } }) => {
                        return (
                          <Select
                            key={`attendant_template.${index}.template_id`}
                            inputRef={ref}
                            options={templatesDataArr}
                            id="template_id"
                            placeholder="Select Template"
                            defaultValue={
                              templatesDataArr.find(
                                (selected: { label: string; value: string }) =>
                                  selected.value === value
                              ) ?? ""
                            }
                            value={templatesDataArr.find(
                              (selected: { label: string; value: string }) =>
                                selected.value === value
                            )}
                            onChange={(selected: { label: string; value: string }) => {
                              onChange(selected?.value);
                            }}
                            error={get(errors, `attendant_template.${index}.template_id`, "")}
                          />
                        );
                      }}
                    />
                    {get(
                      errors,
                      `attendant_template.${index}.template_id`,
                      ""
                    ) ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(
                            errors,
                            `attendant_template.${index}.template_id.message`,
                            ""
                          )}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  {watch("is_package") ? (
                    <div className="mt-4">
                      <Label
                        id={`attendant_template.${index}.name`}
                        name="Form name displayed"
                      />
                      <div className="flex flex-col gap-y-4">
                        {eventDetailData.languages.map((lang) => (
                          <div key={lang}>
                            <div className="relative">
                              <Input
                                id={`attendant_template.${index}.name.${lang}`}
                                type="text"
                                placeholder={`Form name displayed (${lang})`}
                                className="pr-11"
                                {...register(`attendant_template.${index}.name.${lang}`)}
                                error={get(
                                  errors,
                                  `attendant_template.${index}.name.${lang}.message`,
                                  ""
                                )}
                              />
                              <img
                                src={`/images/flags/circle-${lang}.png`}
                                alt="Language Icon"
                                className="absolute top-1/2 right-3 -translate-y-1/2"
                              />
                            </div>
                            {get(
                              errors,
                              `attendant_template.${index}.name.${lang}`,
                              ""
                            ) ? (
                              <p className="error-text">
                                <span className="font-medium">
                                  {get(
                                    errors,
                                    `attendant_template.${index}.name.${lang}.message`,
                                    ""
                                  )}
                                </span>
                              </p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : <></>}
                </div>
              ))}
              {get(
                errors,
                `attendant_template`,
                ""
              ) ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(
                      errors,
                      `attendant_template.message`,
                      ""
                    )}
                  </span>
                </p>
              ) : null}
            </div>
          </div>
          <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
            <p className="text-sm">Advance Settings</p>
          </div>
          <div className="col-span-full flex flex-col items-start gap-y-4">
            <Controller
              control={control}
              name="hide_in_ui"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton label="Show in PROD" checked={!value} onChange={(isChedked) => onChange(!isChedked)} />
              )}
            />
            <Controller
              control={control}
              name="is_soldout"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton label="Is it Sold Out ?" checked={value} onChange={onChange} />
              )}
            />
            {watch("layout_type") === SeatLayoutType.ZONE ? (
              <>
                <Controller
                  control={control}
                  name="is_auto_seating"
                  render={({ field: { onChange, value, ref } }) => (
                    <SwitchButton 
                      label="Auto-seating"
                      disabled={isOpenRegistration && (defaultValues ? true : false)}
                      hint="Used to automatically assign seat numbers for tickets when the user successfully purchases a ticket. And cannot edit after the event has been launched for sale"
                      checked={value}
                      onChange={(isChecked: boolean) => {
                        onChange(isChecked);
                        if (!isChecked) {
                          _resetAutoSeatingFields();
                        } else {
                          if (!watch("start_seat_number")) {
                            setValue("start_seat_number", 1);
                          }
                        }
                      }}
                    />
                  )}
                />
                {watch("is_auto_seating") ? (
                  <div className="col-span-full w-full grid sm:grid-cols-6 gap-y-6 gap-x-4">
                    <div className="sm:col-span-3">
                      <Label
                        id="start_seat_number"
                        name="Minimum number of seats"
                        hint="Enter the smallest seat number to start auto-seating seat numbering"
                        required
                      />
                      <Input
                        id="start_seat_number"
                        type="number"
                        min={1}
                        placeholder={`Minimum number of seats`}
                        disabled={isOpenRegistration && (defaultValues ? true : false)}
                        {...register(`start_seat_number`, {
                          valueAsNumber: true
                        })}
                        error={get(
                          errors,
                          `start_seat_number.message`,
                          ""
                        )}
                      />
                      {get(
                        errors,
                        `start_seat_number`,
                        ""
                      ) ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(
                              errors,
                              `start_seat_number.message`,
                              ""
                            )}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    <div className="sm:col-span-3">
                      <Label
                        id="auto_seating_leading_zero_spaces"
                        name="Total number of characters"
                        hint="Set the total number of characters for the auto-seating seat numbers. Up to 6"
                      />
                      <Input
                        id="auto_seating_leading_zero_spaces"
                        type="number"
                        min={1}
                        placeholder="Total number of characters"
                        disabled={isOpenRegistration && (defaultValues ? true : false)}
                        {...register(`auto_seating_leading_zero_spaces`, {
                          valueAsNumber: true
                        })}
                        error={get(
                          errors,
                          `auto_seating_leading_zero_spaces.message`,
                          ""
                        )}
                      />
                      {get(
                        errors,
                        `auto_seating_leading_zero_spaces`,
                        ""
                      ) ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(
                              errors,
                              `auto_seating_leading_zero_spaces.message`,
                              ""
                            )}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    <div className="col-span-full">
                      <Controller
                        control={control}
                        name="auto_seating_add_characters"
                        render={({ field: { onChange, value, ref } }) => (
                          <SwitchButton 
                            label="Prefix characters"
                            disabled={isOpenRegistration && (defaultValues ? true : false)}
                            hint="Setting up the character string to be associated with the auto-seating seat number. Up to 10 characters"
                            checked={value}
                            onChange={(isChecked: boolean) => {
                              onChange(isChecked);
                              if (!isChecked) {
                                setValue("auto_seating_character_position", "before");
                                setValue("auto_seating_characters", "");
                              } else {
                                if (!watch("auto_seating_character_position")) {
                                  setValue("auto_seating_character_position", "before");
                                }
                              }
                            }}
                          />
                        )}
                      />
                    </div>
                    {watch("auto_seating_add_characters") ? (
                      <>
                        <div className="col-span-full">
                          <div>
                            <Controller
                              control={control}
                              name="auto_seating_character_position"
                              render={({ field: { onChange, value = "", ref } }) => {
                                return (
                                  <div className="flex flex-col sm:flex-row gap-4">
                                    {AUTO_SEATING_CHARACTER_POSITIONS.map(type => (
                                      <RadioButton
                                        disabled={isOpenRegistration && (defaultValues ? true : false)}
                                        checked={type.value === value}
                                        onChange={() => {
                                          onChange(type.value);
                                        }}
                                        item={{
                                          id: type.value,
                                          title: type.label,
                                        }}
                                        key={type.value}
                                      />
                                    ))}
                                  </div>
                                );
                              }}
                            />
                            {get(
                              errors,
                              `auto_seating_character_position`,
                              ""
                            ) ? (
                              <p className="error-text">
                                <span className="font-medium">
                                  {get(
                                    errors,
                                    `auto_seating_character_position.message`,
                                    ""
                                  )}
                                </span>
                              </p>
                            ) : null}
                          </div>
                        </div>
                        <div className="sm:col-span-full">
                          <Label
                            id="auto_seating_characters"
                            name="Character string"
                            required
                          />
                          <Input
                            id="auto_seating_characters"
                            type="text"
                            placeholder="Character string"
                            disabled={isOpenRegistration && (defaultValues ? true : false)}
                            {...register(`auto_seating_characters`)}
                            error={get(
                              errors,
                              `auto_seating_characters.message`,
                              ""
                            )}
                          />
                          {get(
                            errors,
                            `auto_seating_characters`,
                            ""
                          ) ? (
                            <p className="error-text">
                              <span className="font-medium">
                                {get(
                                  errors,
                                  `auto_seating_characters.message`,
                                  ""
                                )}
                              </span>
                            </p>
                          ) : null}
                        </div>
                      </>
                    ) : <></>}
                  </div>
                ) : <></>}
              </>
            ) : <></>}
          </div>
        </div>
      </div>
    </RightSideModal>
  )
}